import { useRightyStore } from '../stores/rightyStore'
import type { RightyNestedContext } from '../types'

/**
 * Composable for managing Righty context in drawer-hosting components.
 *
 * When a component shows nested content in a drawer (e.g., an Offer inside a Flow),
 * it should use this composable to inform Righty of what's being displayed.
 *
 * @example
 * ```typescript
 * const { updateDrawerContext, clearDrawerContext } = useDrawerContext()
 *
 * // When drawer opens with an offer
 * watch(activeNode, (node) => {
 *   if (node?.type === 'offer') {
 *     updateDrawerContext({
 *       editingType: 'offer',
 *       nodeId: node._uuid,
 *       resourceId: node.offerId,
 *       resourceName: offer?.name,
 *       isForm: node.desiredType === 'form'
 *     })
 *   }
 * })
 *
 * // When drawer closes
 * function onDrawerHide() {
 *   clearDrawerContext()
 * }
 * ```
 */
export function useDrawerContext() {
  const rightyStore = useRightyStore()

  /**
   * Update the nested context to reflect what's being shown in the drawer.
   * Call this when opening a drawer or when the drawer content changes.
   */
  function updateDrawerContext(context: RightyNestedContext) {
    rightyStore.setNestedContext(context)
  }

  /**
   * Clear the nested context when the drawer closes.
   * This restores Righty to the base route context.
   */
  function clearDrawerContext() {
    rightyStore.clearNestedContext()
  }

  return {
    updateDrawerContext,
    clearDrawerContext
  }
}
